import { useMemo } from 'react';
import { useGeoLocation } from 'hooks/useGeoLocation';
import type { PlayerProfileVenue } from 'screens/PlayerProfile/types';
import VenueLocationsMap from 'components/VenueLocationsMap';
import type { MapVenue } from 'components/VenueLocationsMap';
import ProfileSection from '../ProfileSection';

interface Props {
  venues: PlayerProfileVenue[];
  isViewerProfile: boolean;
  onEdit?: () => void;
}

/** Stops a lone court, or several at one address, filling the frame with rooftops. */
const MAX_ZOOM = 14;

/** Hasura serialises `numeric` as a string often enough that raw arithmetic on
 *  latitude would silently concatenate instead of adding. */
const toMapVenue = (venue: PlayerProfileVenue): MapVenue | null => {
  const latitude = Number(venue.latitude);
  const longitude = Number(venue.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return {
    id: venue.id,
    title: venue.title,
    address: venue.addressString || venue.title,
    latitude,
    longitude,
    imageUrl: venue.images[0]?.url,
    isIndoor: venue.indoorCourtCount != null ? !!venue.indoorCourtCount : null,
  };
};

export default function ProfilePreferredCourts({ venues, isViewerProfile, onEdit }: Props) {
  // Unlike the coach page there's no coach location to measure from, so distances
  // are "from you" — the viewer's own position, exact or estimated.
  const { centerLatitude, centerLongitude } = useGeoLocation();

  const mapVenues = useMemo(
    () => venues.map(toMapVenue).filter((venue): venue is MapVenue => !!venue),
    [venues],
  );

  // Both are 0 until the viewer's location resolves; measuring then would print
  // a distance from the Gulf of Guinea.
  const distanceFrom =
    centerLatitude && centerLongitude
      ? { latitude: centerLatitude, longitude: centerLongitude }
      : undefined;

  return (
    <ProfileSection
      title="Preferred courts"
      titleSuffix={
        venues.length ? (
          <span className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {` • ${venues.length} ${venues.length === 1 ? 'location' : 'locations'}`}
          </span>
        ) : null
      }
      isViewerProfile={isViewerProfile}
      onEdit={onEdit}
      isEmpty={!venues.length}
      emptyLabel="Add locations"
      emptySize="lg"
    >
      <VenueLocationsMap
        venues={mapVenues}
        distanceFrom={distanceFrom}
        fitToMarkers
        maxZoom={MAX_ZOOM}
        mapClassName="h-[18.75rem] md:h-[20.625rem]"
        // Where someone plays is an illustration, not a tool, so the controls are off.
        options={{
          gestureHandling: 'cooperative',
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          rotateControl: false,
          scaleControl: false,
          clickableIcons: false,
        }}
      />
    </ProfileSection>
  );
}
